<template>
    <div class="vrm-modal vrm-modal--center vrm-modal--wide mfp-with-anim">
        <div class="vrm-modal__content">
            <span class="vrm-modal__title-icon sg-vrm-icon_110_unlock-16px"></span>
            <div class="vrm-modal__title">
                {{
                    $t('users.access_levels', {
                        i18nFallback: 'Access Levels',
                    })
                }}
            </div>
            <br />
            <p>
                {{
                    $t('users.access_levels_description', {
                        i18nFallback:
                            'Note: VRM considers the highest level of access a user has, whether that comes from individual access or team access.',
                    })
                }}
            </p>
            <vrm-scroll-fade class="vrm-scroll-fade--scroll-y">
                <vrm-foldable-table
                    :items="permissions"
                    key-by="id"
                    class="vrm-table--compact vrm-table--borderless vrm-table__first-col--start vrm-table--sticky-header vrm-table--sticky-first-col"
                >
                    <template v-slot:thead>
                        <tr>
                            <th>{{ $t('users.feature', { i18nFallback: 'Feature' }) }}</th>
                            <th>
                                {{
                                    $t('users.user_read_only', { i18nFallback: 'User (read only)' })
                                }}
                            </th>
                            <th>{{ $t('users.technician', { i18nFallback: 'Technician' }) }}</th>
                            <th>{{ $t('users.owner', { i18nFallback: 'Admin' }) }}</th>
                        </tr>
                    </template>
                    <template v-slot:main="{ item: permission }">
                        <td>
                            {{
                                $t(`users.permissions.${permission.id}`, {
                                    i18nFallback: permission.i18nFallback,
                                })
                            }}
                        </td>
                        <td
                            v-html="parseAccess(permission[vrmInstallationAccessLevel.MONITORING])"
                        ></td>
                        <td
                            v-html="parseAccess(permission[vrmInstallationAccessLevel.TECHNICIAN])"
                        ></td>
                        <td
                            v-html="
                                parseAccess(permission[vrmInstallationAccessLevel.FULL_CONTROL])
                            "
                        ></td>
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div class="sg-btn__container">
                <button class="sg-btn sg-blue" @click="onClose">
                    {{ $t('common.close', { i18nFallback: 'Close' }) }}
                </button>
            </div>
            <modal-close-handle />
        </div>
    </div>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade';
import { vrmInstallationAccessLevel } from '@/vue/store/modules/installation-access-level';

@Component({
    components: { ModalCloseHandle, VrmFoldableTable, VrmScrollFade },
})
@AngularLocaleBundle('users')
@AngularLocaleBundle('common')
export default class UserRolesGuideModal extends Vue {
    vrmInstallationAccessLevel = vrmInstallationAccessLevel;

    access = {
        YES: 0,
        READ_ONLY: 1,
        READ_ONLY_EXCEPT_SELF: 2,
        ONLY_SELF: 3,
        NO: 4,
        HIDDEN: 5,
    };

    allRolesAllowed = {
        [this.vrmInstallationAccessLevel.MONITORING]: this.access.YES,
        [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.YES,
        [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
    };

    noMonitoring = {
        [this.vrmInstallationAccessLevel.MONITORING]: this.access.NO,
        [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.YES,
        [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
    };

    readOnlyMonitoring = {
        [this.vrmInstallationAccessLevel.MONITORING]: this.access.READ_ONLY,
        [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.YES,
        [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
    };

    onlyAdmin = {
        [this.vrmInstallationAccessLevel.MONITORING]: this.access.NO,
        [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.NO,
        [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
    };

    get permissions() {
        return [
            { id: 'dashboard', i18nFallback: 'Dashboard', ...this.allRolesAllowed },
            { id: 'dashboard_controls', i18nFallback: 'Dashboard Controls', ...this.noMonitoring },
            { id: 'advanced', i18nFallback: 'Advanced', ...this.readOnlyMonitoring },
            { id: 'device_list', i18nFallback: 'Device List', ...this.readOnlyMonitoring },
            { id: 'venus_os_large', i18nFallback: 'Venus OS Large', ...this.noMonitoring },
            { id: 'general_settings', i18nFallback: 'General settings', ...this.noMonitoring },
            { id: 'tags_settings', i18nFallback: 'Tags settings', ...this.noMonitoring },
            { id: 'location_settings', i18nFallback: 'Location settings', ...this.noMonitoring },
            { id: 'geofence_settings', i18nFallback: 'Geofence settings', ...this.noMonitoring },
            {
                id: 'alarm_rules',
                i18nFallback: 'Alarm Rules',
                [this.vrmInstallationAccessLevel.MONITORING]: this.access.ONLY_SELF,
                [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.YES,
                [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
            },
            {
                id: 'realtime_updates_settings',
                i18nFallback: 'Realtime Updates settings',
                ...this.noMonitoring,
            },
            {
                id: 'inverter_charger_control',
                i18nFallback: 'Inverter/Charger Control',
                ...this.noMonitoring,
            },
            {
                id: 'users',
                i18nFallback: 'Users',
                [this.vrmInstallationAccessLevel.MONITORING]: this.access.READ_ONLY,
                [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.READ_ONLY_EXCEPT_SELF,
                [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
            },
            {
                id: 'delete_this_installation',
                i18nFallback: 'Delete this installation',
                ...this.onlyAdmin,
            },
            {
                id: 'replace_the_gx_device_of_this_installation',
                i18nFallback: 'Replace the GX device of this installation',
                ...this.onlyAdmin,
            },
            { id: 'remote_console', i18nFallback: 'Remote console', ...this.noMonitoring },
            { id: 'notes', i18nFallback: 'Notes', ...this.readOnlyMonitoring },
            { id: 'photos', i18nFallback: 'Photos', ...this.readOnlyMonitoring },
            { id: 'share', i18nFallback: 'Share', ...this.noMonitoring },
            { id: 'alarm_logs', i18nFallback: 'Alarm logs', ...this.readOnlyMonitoring },
            { id: 'event_logs', i18nFallback: 'Event logs', ...this.readOnlyMonitoring },
            { id: 'reporting', i18nFallback: 'Reporting', ...this.allRolesAllowed },
            {
                id: 'diagnostics',
                i18nFallback: 'Diagnostics',
                [this.vrmInstallationAccessLevel.MONITORING]: this.access.HIDDEN,
                [this.vrmInstallationAccessLevel.TECHNICIAN]: this.access.YES,
                [this.vrmInstallationAccessLevel.FULL_CONTROL]: this.access.YES,
            },
            { id: 'firmware_update', i18nFallback: 'Firmware update', ...this.noMonitoring },
            { id: 'remote_veconfigure', i18nFallback: 'Remote VEconfigure', ...this.noMonitoring },
        ];
    }

    onClose() {
        this.$emit('close');
    }

    parseAccess(access) {
        switch (access) {
            case this.access.YES:
                return '<span class="sg-vrm-icon_054_check-16px sg-color-green"></span>';
            case this.access.READ_ONLY:
                return this.$t('users.read_only', { i18nFallback: 'Read only' });
            case this.access.READ_ONLY_EXCEPT_SELF:
                return this.$t('users.read_only_except_self', {
                    i18nFallback: 'Read only - Except self',
                });
            case this.access.ONLY_SELF:
                return this.$t('users.only_for_themselves', {
                    i18nFallback: 'Only for themselves',
                });
            case this.access.NO:
                return '<span class="sg-vrm-icon_055_cross-16px sg-color-red"></span>';
            case this.access.HIDDEN:
                return this.$t('users.hidden', { i18nFallback: 'Hidden' });
            default:
                return '';
        }
    }
}
</script>
